/**
 * Types are structural, as in `workflowGraph.ts`: the agents install this
 * package with bun, where `@amoreg/contracts` is not a workspace member.
 *
 * @module workflowEdit
 */
import { CONDITION_OUTLETS, type WorkflowGraphEdgeLike, workflowStepBranches } from "./workflowGraph.ts";

export const WORKFLOW_STEP_LABEL = {
  start: "Start",
  prompt: "Prompt",
  condition: "Condition",
  "for-each": "For each row",
  compare: "Compare",
  "browser-task": "Browser task",
  intake: "Intake",
  ingestion: "Ingestion",
  approval: "Approval",
  checklist: "Manual step",
  chat: "Open a chat",
  notify: "Notify",
  "save-file": "Save to space",
  email: "Send email",
  output: "Output",
} as const;

export type WorkflowStepType = keyof typeof WORKFLOW_STEP_LABEL;

/** A step's kind as a noun in a sentence: "Prompt step", and "Manual step" rather than "Manual step step". */
export const stepKindNoun = (type: WorkflowStepType): string => {
  const label = WORKFLOW_STEP_LABEL[type];
  return label.endsWith(" step") ? label : `${label} step`;
};

/** The longest step name (the wire limit, `WORKFLOW_LIMITS.labelLength`). */
export const MAX_STEP_LABEL_LENGTH = 120;

export interface WorkflowEditPosition {
  readonly x: number;
  readonly y: number;
}

export interface WorkflowEditNode {
  readonly id: string;
  readonly type: WorkflowStepType;
  readonly label: string;
  readonly position: WorkflowEditPosition;
  readonly config: object;
}

export type WorkflowEditEdge = WorkflowGraphEdgeLike;

export interface WorkflowEditGraph<N extends WorkflowEditNode = WorkflowEditNode> {
  readonly nodes: ReadonlyArray<N>;
  readonly edges: ReadonlyArray<WorkflowEditEdge>;
  readonly layout?: "down" | undefined;
}

const ID_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

const idSuffix = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(6)), (byte) => ID_ALPHABET.charAt(byte % ID_ALPHABET.length)).join("");

/**
 * An id no step in the graph has yet: `prompt-k3f9q2`. Random rather than the
 * next free number, so two writers adding a step to the same version mint
 * distinct ids.
 */
export function nextStepId(graph: WorkflowEditGraph, type: WorkflowStepType): string {
  const taken = new Set(graph.nodes.map((node) => node.id));
  for (;;) {
    const id = `${type}-${idSuffix()}`;
    if (!taken.has(id)) return id;
  }
}

/**
 * A name no step has yet: "Prompt", then "Prompt 2", … Never longer than a
 * step's name may be: a long base is cut short to make room for the number.
 */
export function nextStepLabel(graph: WorkflowEditGraph, base: string): string {
  const taken = new Set(graph.nodes.map((node) => node.label.trim().toLowerCase()));
  const whole = base.slice(0, MAX_STEP_LABEL_LENGTH).trimEnd();
  if (!taken.has(whole.toLowerCase())) return whole;
  for (let index = 2; ; index += 1) {
    const suffix = ` ${index}`;
    const label = `${base.slice(0, MAX_STEP_LABEL_LENGTH - suffix.length).trimEnd()}${suffix}`;
    if (!taken.has(label.toLowerCase())) return label;
  }
}

const STEP_DEFAULTS = {
  start: {},
  output: {},
  prompt: { instructions: "", model: "standard" },
  condition: { mode: "ask", question: "", text: "", model: "standard" },
  "for-each": { source: { step: "", table: "" }, instructions: "", model: "standard", maxRows: 25 },
  compare: { left: { step: "", table: "" }, right: { step: "", table: "" }, key: "", rightKey: "", rules: [], missing: true },
  "browser-task": { taskId: "", inputs: "" },
  approval: { approverEmails: [], instructions: "" },
  checklist: { assigneeEmails: [], instructions: "" },
  ingestion: { fields: [], template: "", model: "standard" },
  intake: { sources: [] },
  chat: { title: "" },
  notify: { recipientEmails: [], title: "" },
  "save-file": { spaceId: "", title: "" },
  email: { recipientEmails: [], subject: "" },
} as const satisfies Record<WorkflowStepType, object>;

export type NewWorkflowStep = {
  readonly [T in WorkflowStepType]: {
    readonly id: string;
    readonly type: T;
    readonly label: string;
    readonly position: WorkflowEditPosition;
    readonly config: (typeof STEP_DEFAULTS)[T];
  };
}[WorkflowStepType];

export function newStep(graph: WorkflowEditGraph, type: WorkflowStepType, position: WorkflowEditPosition): NewWorkflowStep {
  return {
    id: nextStepId(graph, type),
    type,
    label: nextStepLabel(graph, WORKFLOW_STEP_LABEL[type]),
    position,
    config: structuredClone(STEP_DEFAULTS[type]),
  } as NewWorkflowStep;
}

/** The outlets a step leaves by: a Condition's or a Compare step's Yes and No, or every other step's single one (null). */
export function stepOutlets(type: WorkflowStepType): typeof CONDITION_OUTLETS | null {
  return workflowStepBranches(type) ? CONDITION_OUTLETS : null;
}

const reaches = (edges: ReadonlyArray<WorkflowEditEdge>, from: string, to: string): boolean => {
  const next = new Map<string, string[]>();
  for (const edge of edges) next.set(edge.source, [...(next.get(edge.source) ?? []), edge.target]);
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (id === to) return true;
    if (seen.has(id)) continue;
    seen.add(id);
    stack.push(...(next.get(id) ?? []));
  }
  return false;
};

export type LinkRefusal = "self" | "into-start" | "from-output" | "duplicate" | "loop" | "missing" | "outlet";

/** Whether the editor accepts a new link; null when it does, else why not. */
export function linkRefusal(
  graph: WorkflowEditGraph,
  source: string,
  target: string,
  sourceHandle?: string | null,
): LinkRefusal | null {
  const from = graph.nodes.find((node) => node.id === source);
  const to = graph.nodes.find((node) => node.id === target);
  if (!from || !to) return "missing";
  if (source === target) return "self";
  if (to.type === "start") return "into-start";
  if (from.type === "output") return "from-output";
  const outlets: ReadonlyArray<string> | null = stepOutlets(from.type);
  const leavesByOutlet = outlets === null ? sourceHandle == null : !!sourceHandle && outlets.includes(sourceHandle);
  if (!leavesByOutlet) return "outlet";
  if (graph.edges.some((edge) => edge.source === source && edge.target === target && (edge.sourceHandle ?? null) === (sourceHandle ?? null))) {
    return "duplicate";
  }
  if (reaches(graph.edges, target, source)) return "loop";
  return null;
}

export function addLink<N extends WorkflowEditNode>(
  graph: WorkflowEditGraph<N>,
  source: string,
  target: string,
  sourceHandle?: string | null,
): WorkflowEditGraph<N> {
  if (linkRefusal(graph, source, target, sourceHandle) !== null) return graph;
  const id = `${source}-${sourceHandle ?? "out"}-${target}`;
  const edge: WorkflowEditEdge = sourceHandle ? { id, source, target, sourceHandle } : { id, source, target };
  return { ...graph, edges: [...graph.edges, edge] };
}

/**
 * A copy of the step `nodeId` at `position`: its settings, a fresh id and
 * name ("Find deadlines 2"), no links. Null for Start (a workflow has one)
 * or a step that is gone.
 */
export function duplicateStep<N extends WorkflowEditNode>(
  graph: WorkflowEditGraph<N>,
  nodeId: string,
  position: WorkflowEditPosition,
): { readonly graph: WorkflowEditGraph<N>; readonly id: string } | null {
  const node = graph.nodes.find((item) => item.id === nodeId);
  if (!node || node.type === "start") return null;
  const copy: N = {
    ...node,
    id: nextStepId(graph, node.type),
    label: nextStepLabel(graph, node.label.trim() || WORKFLOW_STEP_LABEL[node.type]),
    position,
    config: structuredClone(node.config),
  };
  return { graph: { ...graph, nodes: [...graph.nodes, copy] }, id: copy.id };
}

/**
 * `node` put into the link `edgeId`: the link's source now leads to it (by
 * the same outlet) and it leads on to the link's target (a Condition by Yes).
 * The target and every step after it move down by `push` to make room.
 * Null when the link is gone or the step cannot sit there.
 */
export function insertStepOnLink<N extends WorkflowEditNode>(
  graph: WorkflowEditGraph<N>,
  edgeId: string,
  node: N,
  push: number,
): WorkflowEditGraph<N> | null {
  const edge = graph.edges.find((item) => item.id === edgeId);
  if (!edge || node.type === "start" || node.type === "output") return null;
  const outlet = stepOutlets(node.type)?.[0] ?? null;
  const opened: WorkflowEditGraph<N> = { ...graph, nodes: [...graph.nodes, node], edges: graph.edges.filter((item) => item.id !== edgeId) };
  if (linkRefusal(opened, edge.source, node.id, edge.sourceHandle) !== null) return null;
  const into = addLink(opened, edge.source, node.id, edge.sourceHandle);
  if (linkRefusal(into, node.id, edge.target, outlet) !== null) return null;
  const linked = addLink(into, node.id, edge.target, outlet);
  if (push <= 0) return linked;
  const after = new Set<string>();
  const stack = [edge.target];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (after.has(id)) continue;
    after.add(id);
    for (const item of linked.edges) if (item.source === id) stack.push(item.target);
  }
  return moveSteps(
    linked,
    new Map(linked.nodes.filter((item) => after.has(item.id)).map((item) => [item.id, { x: item.position.x, y: item.position.y + push }])),
  );
}

export function removeSteps<N extends WorkflowEditNode>(graph: WorkflowEditGraph<N>, nodeIds: ReadonlySet<string>): WorkflowEditGraph<N> {
  return {
    ...graph,
    nodes: graph.nodes.filter((node) => !nodeIds.has(node.id)),
    edges: graph.edges.filter((edge) => !nodeIds.has(edge.source) && !nodeIds.has(edge.target)),
  };
}

export function updateStep<N extends WorkflowEditNode>(
  graph: WorkflowEditGraph<N>,
  nodeId: string,
  update: (node: N) => N,
): WorkflowEditGraph<N> {
  return { ...graph, nodes: graph.nodes.map((node) => (node.id === nodeId ? update(node) : node)) };
}

export function moveSteps<N extends WorkflowEditNode>(
  graph: WorkflowEditGraph<N>,
  positions: ReadonlyMap<string, WorkflowEditPosition>,
): WorkflowEditGraph<N> {
  if (positions.size === 0) return graph;
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const position = positions.get(node.id);
      return position ? { ...node, position } : node;
    }),
  };
}

/**
 * The graph as it is saved: a step whose name was cleared takes its kind's
 * name, and names lose surrounding spaces (the server refuses empty ones).
 */
export function graphForSave<N extends WorkflowEditNode>(graph: WorkflowEditGraph<N>): WorkflowEditGraph<N> {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const label = node.label.trim() || WORKFLOW_STEP_LABEL[node.type];
      return label === node.label ? node : { ...node, label };
    }),
  };
}

export const graphsEqual = (left: WorkflowEditGraph, right: WorkflowEditGraph) => JSON.stringify(left) === JSON.stringify(right);
